import { computed } from "mobx";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo } from "react";
import { Layer, Source } from "react-map-gl";
import { useDrawMapStore, useFavoriteStore, useMapStore } from "../..";
import useFavoritePropertiesWithFocusArea from "../../hooks/useFavoritePropertiesWithFocusArea";
import useNavigateToProperty from "../../hooks/useNavigateToProperty";
import { FavoriteTargetType, IFavoritePropertyDetails } from "../../interfaces/IFavorite";
import ILayerProps from "../../interfaces/IlayerProps";
import GeometryService from "../../services/geometryService";
import { minZoom } from "../mapConfig";

export const favoritePropertyPoligonLayerId = "favoritePropertyPoligonLayer";

function FavoritePropertiesMapLayer({ visible }: ILayerProps) {
    const navigateToProperty = useNavigateToProperty();
    const mapStore = useMapStore();
    const drawMapStore = useDrawMapStore();
    const favoriteStore = useFavoriteStore();
    const geometryService = useMemo(() => new GeometryService(), []);
    const favoriteProperties = useFavoritePropertiesWithFocusArea(useMemo(() => computed(() => favoriteStore.favorites
        .filter(f => f.targetType === FavoriteTargetType.Property)
        .map(f => favoriteStore.favoritePropertiesDetails.find(pd => pd.id === f.targetId))
        .filter(p => p) as IFavoritePropertyDetails[]), []).get());

    useEffect(() => {
        const unsubscribeMouseClick = mapStore.onMouseClick("favorites", feature => {
            if (feature?.layer?.id === favoritePropertyPoligonLayerId && !drawMapStore.isActiveDrawing())
                navigateToProperty(feature.properties!.longitude, feature.properties!.latitude);
        });

        return unsubscribeMouseClick;
    }, []);

    const favoritePropertyPointsGeoJson: any = useMemo(() => computed(() => ({
        type: "FeatureCollection",
        features: favoriteProperties.map(p => ({
            type: "Feature",
            geometry: {
                type: "Point",
                coordinates: [p.centerPoint.longitude, p.centerPoint.latitude],
            },
        })),
    })), [favoriteProperties]).get();

    const favoritePropertyPoligonsGeoJson: any = useMemo(() => computed(() => ({
        type: "FeatureCollection",
        features: favoriteProperties.map(p => ({
            ...geometryService.mapIShapesToPolygonFeatures([p.shapes[0]])[0],
            id: p.id,
            properties: {
                latitude: p.centerPoint.latitude,
                longitude: p.centerPoint.longitude,
            },
        })),
    })), [favoriteProperties]).get();

    if (!mapStore.isStyleLoaded)
        return null;

    return (
        <>
            <Source id="favoritePropertyPoligonSource" type="geojson" data={favoritePropertyPoligonsGeoJson} minzoom={minZoom.favorites}>
                <Layer
                    id={favoritePropertyPoligonLayerId}
                    type="fill"
                    minzoom={minZoom.favorites}
                    paint={{
                        "fill-color": "#F48E29",
                        "fill-opacity": 0.05,
                    }}
                    layout={{
                        "visibility": visible ? "visible" : "none",
                    }}
                />
                <Layer
                    id={"faovritePropertyBorderLayer"}
                    type="line"
                    minzoom={minZoom.favorites}
                    paint={{
                        "line-color": "#F48E29",
                        "line-width": 5,
                    }}
                    layout={{
                        "line-cap": "round",
                        "line-join": "round",
                        "visibility": visible ? "visible" : "none",
                    }}
                />
            </Source>
            <Source id="faovritePropertyPointSource" type="geojson" data={favoritePropertyPointsGeoJson} minzoom={minZoom.favorites}>
                <Layer
                    id={"favoritePropertyPointHaloLayer"}
                    type="symbol"
                    minzoom={minZoom.favorites}
                    paint={{ "icon-color": "white" }}
                    layout={{
                        "icon-size": 1,
                        "icon-allow-overlap": true,
                        "symbol-z-order": "source",
                        "icon-image": "ico-circle",
                        "visibility": visible ? "visible" : "none",
                    }}
                />
                <Layer
                    id={"favoritePropertyPointLayer"}
                    type="symbol"
                    minzoom={minZoom.favorites}
                    paint={{ "icon-color": "#F48E29" }}
                    layout={{
                        "icon-size": 0.9,
                        "icon-allow-overlap": true,
                        "symbol-z-order": "source",
                        "icon-image": "ico-circle",
                        "visibility": visible ? "visible" : "none",
                    }}
                />
            </Source>
        </>
    );
}

export default observer(FavoritePropertiesMapLayer);